import { ErrorLabel, Input } from '@ibsys/kit';
import { XIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@/shared/ui';

type Props = {
  /** Набранный балл */
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Потолок балла блока; null — верхней границы у блока нет */
  maxRating: number | null;
  /** Размерность балла; null — размерность не определена */
  unit?: string | null;
  /** Набранный балл вне границ блока — сохранения не будет */
  isOutOfRange: boolean;
  isPending: boolean;
  onClose: () => void;
};

/**
 * Ввод ручного балла блока: поле с потолком блока и отказом на балл вне границ.
 * Сохранение и отмену ведёт окно правки — здесь только само поле.
 */
export const EvaluationBlockRatingEditor: FC<Props> = ({
  value,
  onChange,
  onKeyDown,
  maxRating,
  unit,
  isOutOfRange,
  isPending,
  onClose,
}) => {
  const { t } = useTranslation(['dialogs']);
  // Без потолка блок принимает любой неотрицательный балл — тогда и отказ говорит только о знаке
  const outOfRangeHint =
    maxRating !== null
      ? t('dialogs:expertReport.ratingOutOfRange', { max: maxRating })
      : t('dialogs:expertReport.ratingNegative');

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          max={maxRating ?? undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          aria-invalid={isOutOfRange}
          disabled={isPending}
          autoFocus
          className="w-20"
        />
        <span className="text-muted-foreground">
          {maxRating !== null ? `/ ${maxRating}` : ''}
          {unit ? ` ${unit}` : ''}
        </span>
        <IconButton
          variant="ghost"
          size="icon"
          className="size-6 ml-auto"
          label={t('dialogs:expertReport.close')}
          icon={<XIcon className="size-3" />}
          onClick={onClose}
        />
      </div>
      {/* Место под подсказку занято всегда и на всю ширину окна: иначе её появление меняет
          высоту окна, и оно перескакивает в другое место экрана */}
      <ErrorLabel className="block min-h-5 text-xs">{isOutOfRange ? outOfRangeHint : null}</ErrorLabel>
    </div>
  );
};
